import React, { useEffect, useRef, useState } from 'react';
import {
  Drawer,
  Form,
  Radio,
  Select,
  Modal,
  Switch,
  Alert,
  Input,
  Button,
  Upload,
  Space,
  Table,
  InputNumber,
  message,
  Divider,
} from 'antd';
import { UploadOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  BaselinePhoto,
  ContentChecklistItem,
  DecisionFlow,
  ReturnInspectionRule,
  RuleMode,
  ScopeLevel,
  SCOPE_LEVEL_LABEL,
} from '../../types/returnInspection';
import { createReturnInspectionRule, uploadBaselinePhoto } from '../../services/returnInspection';
import { getAttachmentUrl } from '../../utils/attachmentUrl';
import {
  APPEARANCE_BAD_OPTIONS,
  SEALED_DECISION_FLOW,
  appearanceOkOptions,
  CONTENT_BAD_OPTIONS,
  CONTENT_OK_OPTIONS,
  DEFAULT_DECISION_FLOW,
  branchContinues,
  contentStageReachable,
  describeFlow,
  withBranchAction,
} from './decisionFlow';
import { LevelBadge } from './ruleDisplay';

interface RuleFormDrawerProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  scopeLevel: ScopeLevel;
  /** 项目级=project_id，库存单元级=inventory_unit_id；客户级不需要 */
  scopeId?: number;
  /** 管理员设置客户级规则时必须指定目标客户；客户主账号不需要（后端强制用自己） */
  customerId?: number;
  /** 展示在标题里，让用户清楚是在给谁配规则，如"项目 P2610 - 华东仓" */
  scopeLabel: string;
  existingRule?: ReturnInspectionRule | null; // 当前生效版本（如有），用于预填表单
}

// 处理方式的文案：把每种处理会产生的费用/流程讲清楚，方便客户权衡。
const MODE_TEXT: Record<RuleMode, { label: string; hint: string }> = {
  always_destroy: {
    label: '直接销毁',
    hint: '一般适用于低货值产品，不执行任何检测直接销毁，避免产生上架费用/检测费用/仓储费用，可能产生销毁费用。',
  },
  always_aiu: {
    label: '直接作为异常库存（AIU）入库',
    hint: '不执行任何检测，留待以后批量检测处理（费用更低），会正常产生上架费/仓储费。',
  },
  conditional: {
    label: '按以下流程进行初检：',
    hint: '先检查外观，必要时再检查内件，每个检查结果之后做什么由你在下方设置。',
  },
};

// 只有库存单元级规则能选"按流程初检"：外观/内件基准描述的是某一种具体货物，通用规则和项目级规则
// 覆盖多种库存单元，没有统一的基准（后端也会拒绝）。
// 吸塑/塑封开关旁的说明：判定标准改成"包装是否完好"，要求严格二元（有任何痕迹就不算一致）
const SEALED_SWITCH_HINT =
  '打开此开关后，仓库按"吸塑/塑封包装是否完好"判定外观是否一致：没有任何拆封、破损、鼓包痕迹才算外观一致。完好的吸塑包装无法拆开，因此"外观一致"之后不能继续检查内件。';

// 基准照片都是选填，张数上限和后端保持一致
const MAX_PACKAGING_PHOTOS = 5;
const MAX_CONTENT_PHOTOS = 10;

const UNIT_MODE_OPTIONS: RuleMode[] = ['always_destroy', 'always_aiu', 'conditional'];
const UPPER_MODE_OPTIONS: RuleMode[] = ['always_destroy', 'always_aiu'];

const PhotoList: React.FC<{
  photos: BaselinePhoto[];
  onChange: (photos: BaselinePhoto[]) => void;
  disabled?: boolean;
  /** 最多能上传几张，满了就不能再传 */
  max: number;
}> = ({ photos, onChange, disabled, max }) => {
  const [uploading, setUploading] = useState(false);
  const full = photos.length >= max;

  // 上传期间用户可能删照片，所以保存完成时要拿最新的列表，不能用发起上传时的旧值
  const latestPhotos = useRef(photos);
  latestPhotos.current = photos;

  // 一次可以选多张：antd 会对选中的每个文件各调用一次 beforeUpload，并把整批文件（fileList）一起传进来。
  // 只在处理这一批的第一个文件时统一处理整批，其余的直接跳过；超出剩余张数的部分忽略并提示。
  const handleUpload = async (file: File, fileList: File[]) => {
    if (fileList && fileList[0] !== file) return false;
    const batch = fileList && fileList.length ? fileList : [file];
    const remaining = max - latestPhotos.current.length;
    if (remaining <= 0) {
      message.warning(`最多上传 ${max} 张照片`);
      return false;
    }
    const accepted = batch.slice(0, remaining);
    if (batch.length > remaining) {
      message.warning(`最多上传 ${max} 张照片，已忽略多选的 ${batch.length - remaining} 张`);
    }
    setUploading(true);
    try {
      const results = await Promise.allSettled(accepted.map((item) => uploadBaselinePhoto(item)));
      const urls = results.flatMap((result) =>
        result.status === 'fulfilled' ? [result.value] : [],
      );
      if (urls.length) {
        onChange(
          [...latestPhotos.current, ...urls.map((url) => ({ url, caption: '' }))].slice(0, max),
        );
      }
      const failedCount = results.length - urls.length;
      if (failedCount) {
        message.error(`有 ${failedCount} 张图片上传失败，请重试`);
      }
    } finally {
      setUploading(false);
    }
    return false; // 阻止 antd Upload 自己再发一次请求
  };

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
        {photos.map((p, idx) => (
          <div key={`${p.url}-${idx}`} style={{ position: 'relative', width: 96 }}>
            <img
              src={getAttachmentUrl({ url: p.url })}
              alt={p.caption || `参考图${idx + 1}`}
              style={{
                width: 96,
                height: 96,
                objectFit: 'cover',
                borderRadius: 6,
                border: '1px solid #d9d9d9',
              }}
            />
            <Button
              size="small"
              danger
              type="text"
              icon={<DeleteOutlined />}
              disabled={disabled}
              style={{
                position: 'absolute',
                top: 2,
                right: 2,
                background: 'rgba(255,255,255,0.85)',
              }}
              onClick={() => onChange(photos.filter((_, i) => i !== idx))}
            />
          </div>
        ))}
      </div>
      <Upload
        multiple
        beforeUpload={handleUpload}
        showUploadList={false}
        disabled={disabled || uploading || full}
        accept="image/*"
      >
        <Button icon={<UploadOutlined />} loading={uploading} disabled={disabled || full}>
          上传参考照片
        </Button>
      </Upload>
    </div>
  );
};

const ChecklistEditor: React.FC<{
  rows: ContentChecklistItem[];
  onChange: (rows: ContentChecklistItem[]) => void;
  disabled?: boolean;
}> = ({ rows, onChange, disabled }) => {
  return (
    <div>
      <Table
        size="small"
        pagination={false}
        rowKey={(_, idx) => String(idx)}
        dataSource={rows}
        columns={[
          {
            title: '物品名称',
            dataIndex: 'item',
            render: (_, row, idx) => (
              <Input
                value={row.item}
                disabled={disabled}
                placeholder="如：主机 / 说明书 / 充电线"
                onChange={(e) => {
                  const next = [...rows];
                  next[idx] = { ...next[idx], item: e.target.value };
                  onChange(next);
                }}
              />
            ),
          },
          {
            title: '数量',
            dataIndex: 'qty',
            width: 120,
            render: (_, row, idx) => (
              <InputNumber
                min={1}
                value={row.qty}
                disabled={disabled}
                onChange={(v) => {
                  const next = [...rows];
                  next[idx] = { ...next[idx], qty: Number(v) || 1 };
                  onChange(next);
                }}
              />
            ),
          },
          {
            title: '',
            width: 48,
            render: (_, __, idx) => (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={disabled}
                onClick={() => onChange(rows.filter((_, i) => i !== idx))}
              />
            ),
          },
        ]}
      />
      <Button
        type="dashed"
        block
        icon={<PlusOutlined />}
        disabled={disabled}
        style={{ marginTop: 8 }}
        onClick={() => onChange([...rows, { item: '', qty: 1 }])}
      >
        添加一行
      </Button>
    </div>
  );
};

/** 一行"结果 → 处置"：左边是检查结果，右边下拉选处置 */
/** 后端校验错误可能是多层嵌套的（如 {packaging_baseline_photos: [{url: ['…']}]}），递归取出第一条文字提示 */
const firstErrorText = (data: unknown): string => {
  if (typeof data === 'string') return data;
  if (Array.isArray(data)) {
    for (const item of data) {
      const text = firstErrorText(item);
      if (text) return text;
    }
    return '';
  }
  if (data && typeof data === 'object') {
    for (const value of Object.values(data as Record<string, unknown>)) {
      const text = firstErrorText(value);
      if (text) return text;
    }
  }
  return '';
};

/** 字段标题行：左边标题，右边灰色提示（如"选填，最多5张"） */
const TitleWithHint: React.FC<{ title: string; hint: string }> = ({ title, hint }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 8,
    }}
  >
    <span>{title}</span>
    <span style={{ color: '#999' }}>{hint}</span>
  </div>
);

const FlowSelectRow: React.FC<{
  label: string;
  value?: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}> = ({ label, value, options, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
    <span style={{ width: 96, flexShrink: 0 }}>{label}</span>
    <Select
      value={value}
      options={options}
      onChange={onChange}
      style={{ width: 280, maxWidth: '100%' }}
    />
  </div>
);

const RuleFormDrawer: React.FC<RuleFormDrawerProps> = ({
  open,
  onClose,
  onSaved,
  scopeLevel,
  scopeId,
  customerId,
  scopeLabel,
  existingRule,
}) => {
  const supportsInspection = scopeLevel === 'inventory_unit';
  const modeOptions = supportsInspection ? UNIT_MODE_OPTIONS : UPPER_MODE_OPTIONS;
  const [mode, setMode] = useState<RuleMode>('always_aiu');
  const [packagingPhotos, setPackagingPhotos] = useState<BaselinePhoto[]>([]);
  const [packagingNotes, setPackagingNotes] = useState('');
  const [sealed, setSealed] = useState(false);
  const [flow, setFlow] = useState<DecisionFlow>(DEFAULT_DECISION_FLOW);
  const [contentPhotos, setContentPhotos] = useState<BaselinePhoto[]>([]);
  const [checklist, setChecklist] = useState<ContentChecklistItem[]>([]);
  const [contentNotes, setContentNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (existingRule) {
      // 不该出现的情况：上层规则里没有"初检"选项，万一有历史数据就按"一律AIU入库"预填，保存时不会带基准资料
      setMode(
        !supportsInspection && existingRule.mode === 'conditional'
          ? 'always_aiu'
          : existingRule.mode,
      );
      setPackagingPhotos(existingRule.packaging_baseline_photos || []);
      setPackagingNotes(existingRule.packaging_notes || '');
      setSealed(!!existingRule.packaging_sealed);
      setFlow(
        existingRule.mode === 'conditional' &&
          existingRule.decision_flow &&
          'appearance_ok' in existingRule.decision_flow
          ? (existingRule.decision_flow as DecisionFlow)
          : DEFAULT_DECISION_FLOW,
      );
      setContentPhotos(existingRule.content_baseline_photos || []);
      setChecklist(existingRule.content_checklist || []);
      setContentNotes(existingRule.content_notes || '');
    } else {
      setMode('always_aiu');
      setPackagingPhotos([]);
      setPackagingNotes('');
      setSealed(false);
      setFlow(DEFAULT_DECISION_FLOW);
      setContentPhotos([]);
      setChecklist([]);
      setContentNotes('');
    }
  }, [open, existingRule, supportsInspection]);

  const isConditional = mode === 'conditional';
  // 流程里有没有路径会走到内件检查：没有的话不需要内件基准资料
  const contentReachable = isConditional && contentStageReachable(flow);
  // 内件物品清单的实时统计：种类 = 已填名称的行数，件数 = 这些行的数量之和
  // 没填物品名称的行视为没有（统计和保存都忽略它们，否则后端会因为名称为空拒绝整条规则）
  const filledChecklist = checklist.filter((row) => row.item.trim());
  const checklistKinds = filledChecklist.length;
  const checklistPieces = filledChecklist.reduce((sum, row) => sum + (Number(row.qty) || 0), 0);

  // 流程会检查内件、但内件的照片、物品清单、文字说明三样都没填：仓库没有参照物
  const noContentBaseline =
    contentReachable &&
    contentPhotos.length === 0 &&
    filledChecklist.length === 0 &&
    !contentNotes.trim();

  // 选"销毁"是不可逆的处置：先让客户确认，确认后才真正改动
  const confirmDestroy = (apply: () => void) => {
    Modal.confirm({
      title: '确认选择"直接销毁"？',
      content: '仓库一旦判定为"不一致"，货物将被直接销毁，无法恢复。',
      okText: '确认选择',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: apply,
    });
  };
  // 打开吸塑/塑封开关：完好的包装无法拆开，"外观一致"之后不能继续检查内件，已经选了的自动改成"作为正常产品入库"
  const toggleSealed = (checked: boolean) => {
    setSealed(checked);
    if (checked && flow.appearance_ok.action === 'continue') {
      setFlow((prev) =>
        withBranchAction(prev, 'appearance_ok', SEALED_DECISION_FLOW.appearance_ok.action),
      );
    }
  };
  const chooseBranchAction = (branch: 'appearance_ok' | 'appearance_bad', value: string) => {
    const apply = () => setFlow((prev) => withBranchAction(prev, branch, value));
    if (value === 'destroy') confirmDestroy(apply);
    else apply();
  };
  const chooseContentOutcome = (
    branch: 'appearance_ok' | 'appearance_bad',
    key: 'content_ok' | 'content_bad',
    value: string,
  ) => {
    const apply = () =>
      setFlow((prev) => ({ ...prev, [branch]: { ...prev[branch], [key]: value } }) as DecisionFlow);
    if (value === 'destroy') confirmDestroy(apply);
    else apply();
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const scopeFields =
        scopeLevel === 'customer'
          ? customerId
            ? { customer_id: customerId }
            : {}
          : scopeLevel === 'project'
            ? { project_id: scopeId }
            : { inventory_unit_id: scopeId };
      await createReturnInspectionRule({
        scope_level: scopeLevel,
        ...scopeFields,
        mode,
        packaging_baseline_photos: isConditional ? packagingPhotos : [],
        packaging_notes: isConditional ? packagingNotes : '',
        ...(isConditional ? { decision_flow: flow, packaging_sealed: sealed } : {}),
        content_baseline_photos: contentReachable ? contentPhotos : [],
        content_checklist: contentReachable ? filledChecklist : [],
        content_notes: contentReachable ? contentNotes : '',
      });
      message.success(existingRule ? '已发布新版本规则' : '规则创建成功');
      onSaved();
      onClose();
    } catch (e: any) {
      message.error(firstErrorText(e?.response?.data) || '保存失败，请检查填写内容');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      title={`${existingRule ? '编辑' : '新建'}${SCOPE_LEVEL_LABEL[scopeLevel]}规则 · ${scopeLabel}`}
      open={open}
      onClose={onClose}
      width={640}
      extra={
        <Button type="primary" loading={saving} onClick={handleSave}>
          保存并发布
        </Button>
      }
    >
      {/* 只显示当前这一级的徽标，不显示上下级的优先级序列；具体是谁的规则见标题 */}
      <div style={{ marginBottom: 12 }}>
        <LevelBadge level={scopeLevel} />
      </div>
      <Form layout="vertical">
        <Form.Item label="处理模式">
          <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)}>
            <Space direction="vertical">
              {modeOptions.map((m) => (
                <Radio key={m} value={m}>
                  {MODE_TEXT[m].label}
                  <span style={{ color: '#999', marginLeft: 8 }}>{MODE_TEXT[m].hint}</span>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
          {isConditional && (
            <div style={{ marginTop: 12 }}>
              请在下方设置检查流程，并提供此库存单元的外观和内件基准信息
            </div>
          )}
        </Form.Item>

        {isConditional && (
          <>
            <Divider>外观基准</Divider>
            <Form.Item label="外包装为吸塑/塑封包装">
              <Switch checked={sealed} onChange={toggleSealed} />
              <div style={{ color: '#999', fontSize: 12, marginTop: 4 }}>{SEALED_SWITCH_HINT}</div>
            </Form.Item>
            <Form.Item>
              <TitleWithHint title="外观参考照片" hint={`选填，最多${MAX_PACKAGING_PHOTOS}张`} />
              <PhotoList
                photos={packagingPhotos}
                onChange={setPackagingPhotos}
                max={MAX_PACKAGING_PHOTOS}
              />
            </Form.Item>
            <Form.Item label="外观文字说明（可选）">
              <Input.TextArea
                rows={2}
                value={packagingNotes}
                placeholder="如：外包装应为xx材质，封口处应有圆形防伪贴"
                onChange={(e) => setPackagingNotes(e.target.value)}
              />
            </Form.Item>
            <Form.Item label="外观检查之后">
              <Space direction="vertical" style={{ width: '100%' }}>
                <FlowSelectRow
                  label="外观一致 →"
                  value={flow.appearance_ok.action}
                  options={appearanceOkOptions(sealed)}
                  onChange={(v) => chooseBranchAction('appearance_ok', v)}
                />
                <FlowSelectRow
                  label="外观不一致 →"
                  value={flow.appearance_bad.action}
                  options={APPEARANCE_BAD_OPTIONS}
                  onChange={(v) => chooseBranchAction('appearance_bad', v)}
                />
              </Space>
            </Form.Item>

            {contentReachable && (
              <>
                <Divider>内件基准</Divider>
                {/* 三样内件基准资料都是选填；全都没填时只提醒，不阻止保存 */}
                {noContentBaseline && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="未提供任何内件基准资料，内件判定会缺少参照物，将由仓库人员自行判断，或请补充内件基准资料"
                  />
                )}
                <Form.Item>
                  <TitleWithHint title="内件参考照片" hint={`选填，最多${MAX_CONTENT_PHOTOS}张`} />
                  <PhotoList
                    photos={contentPhotos}
                    onChange={setContentPhotos}
                    max={MAX_CONTENT_PHOTOS}
                  />
                </Form.Item>
                <Form.Item>
                  {/* 标题行：左边标题，右边实时统计（只统计已填了名称的行） */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 8,
                    }}
                  >
                    <span>内件物品清单</span>
                    <span style={{ color: '#999' }}>
                      选填，共{checklistKinds}种{checklistPieces}件
                    </span>
                  </div>
                  <ChecklistEditor rows={checklist} onChange={setChecklist} />
                </Form.Item>
                <Form.Item label="内件文字说明（可选）">
                  <Input.TextArea
                    rows={2}
                    value={contentNotes}
                    placeholder="如：主机需配电源线和说明书各一份"
                    onChange={(e) => setContentNotes(e.target.value)}
                  />
                </Form.Item>
                <Form.Item>
                  <Space direction="vertical" style={{ width: '100%' }} size={12}>
                    {(['appearance_ok', 'appearance_bad'] as const)
                      .filter((branch) => branchContinues(flow[branch]))
                      .map((branch) => (
                        <div key={branch}>
                          <div style={{ color: '#595959', marginBottom: 6 }}>
                            {branch === 'appearance_ok'
                              ? '外观一致，继续检查内件：'
                              : '外观不一致，继续检查内件：'}
                          </div>
                          <Space direction="vertical" style={{ width: '100%' }}>
                            <FlowSelectRow
                              label="内件符合 →"
                              value={flow[branch].content_ok}
                              options={CONTENT_OK_OPTIONS}
                              onChange={(v) => chooseContentOutcome(branch, 'content_ok', v)}
                            />
                            <FlowSelectRow
                              label="内件不符 →"
                              value={flow[branch].content_bad}
                              options={CONTENT_BAD_OPTIONS}
                              onChange={(v) => chooseContentOutcome(branch, 'content_bad', v)}
                            />
                          </Space>
                        </div>
                      ))}
                  </Space>
                </Form.Item>
              </>
            )}

            <Divider>最终完整检查流程预览</Divider>
            <div
              style={{ background: '#fafafa', padding: 12, borderRadius: 6, lineHeight: '24px' }}
            >
              {/* 不能用文字当 key：两个分支的内件处置文字可能完全相同，重复的 key 会让 React 更新时留下旧行 */}
              {describeFlow(flow, sealed).map((line, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={index}>{line}</div>
              ))}
              <div style={{ color: '#999', fontSize: 12, marginTop: 6 }}>
                每增加一道检查，会产生相应的检测费用。
              </div>
            </div>
          </>
        )}
      </Form>
    </Drawer>
  );
};

export default RuleFormDrawer;
